import { Check, Gift, Ticket } from "lucide-react";
import { PROFILE, STEPS } from "@/lib/ambassadors";
import { A, Eyebrow, Reveal, btnDark } from "./ui";

const PRODUCTS = ["/products/real/u23-masque-supreme.webp", "/products/real/u01-extreme.webp", "/products/real/u13-huile-locks.webp"];

export function AmbBenefits({ code }) {
  return (
    <section id="avantages" data-testid="amb-benefits" className="scroll-mt-24 bg-[#141013] px-5 py-20 md:px-10 md:py-28">
      <div className="mx-auto max-w-6xl">
        <Reveal><Eyebrow><span className="text-[#E5C88F]">Tes avantages exclusifs</span></Eyebrow>
          <h2 className="font-display text-3xl text-[#FBF8F3] md:text-4xl">Ce que tu reçois <span className="italic text-[#E5C88F]">chaque mois</span></h2></Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <Reveal className="min-w-0">
            <article data-testid="amb-benefit-products" className="flex h-full flex-col rounded-3xl bg-[#FBF8F3] p-8 md:p-10">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#7A5418]"><Gift className="h-4 w-4" /> Avantage n°1</p>
              <h3 className={`mt-4 font-display text-3xl md:text-4xl ${A.ink}`}>Tes 2 produits Ozentya offerts chaque mois</h3>
              <p className={`mt-4 text-base leading-relaxed ${A.muted}`}>Chaque mois, choisis librement 2 soins parmi nos produits à l'unité, sans limite de prix, selon les modalités de disponibilité et de validation de la marque. Les packs Ozentya ne sont pas inclus.</p>
              <p data-testid="amb-benefit-products-highlight" className="mt-6 inline-flex w-fit items-baseline gap-3 rounded-full bg-[#141013] px-6 py-3 text-[#FBF8F3]">
                <span className="font-display text-3xl text-[#E5C88F]">2</span> produits à l'unité au choix — quelle que soit leur valeur</p>
              <div className="mt-8 grid grid-cols-3 gap-3">
                {PRODUCTS.map((src) => <img key={src} src={src} alt="Soin Ozentya" loading="lazy" className="aspect-square w-full rounded-2xl bg-[#F6F0E7] object-cover" />)}
              </div>
            </article>
          </Reveal>
          <Reveal delay={0.08} className="min-w-0">
            <article data-testid="amb-benefit-code" className="flex h-full flex-col rounded-3xl border border-[#E5C88F]/50 bg-[#1E1519] p-8 md:p-10">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#E5C88F]"><Ticket className="h-4 w-4" /> Avantage n°2</p>
              <h3 className="mt-4 font-display text-3xl text-[#FBF8F3] md:text-4xl">Ton code promo exclusif</h3>
              <p className="mt-4 text-base leading-relaxed text-[#E9DFD2]">Bénéficie d'un code promotionnel personnel offrant -20 % à ta communauté sur les produits à l'unité (hors packs), non cumulable avec un autre code.</p>
              <div className="mt-8 rounded-2xl border-2 border-dashed border-[#E5C88F] p-6 text-center">
                <p className="text-xs uppercase tracking-[0.24em] text-[#E9DFD2]">Exemple de code</p>
                <p data-testid="amb-example-code" className="mt-2 break-all font-mono text-3xl font-bold tracking-[0.12em] text-[#E5C88F] sm:text-4xl md:text-5xl md:tracking-[0.2em]">{code}</p>
                <p className="mt-2 font-display text-2xl text-[#FBF8F3]">-20 % pour ta communauté</p>
              </div>
              <p className="mt-6 text-sm leading-relaxed text-[#E9DFD2]">Ton code personnel t'est attribué par Ozentya dès l'acceptation de ta candidature. Il reste actif tant que ta collaboration continue.</p>
              <a href="#conditions" data-testid="amb-benefit-code-cta" className="mt-8 inline-flex h-14 w-fit items-center rounded-full bg-[#E5C88F] px-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#141013] transition-colors duration-300 hover:bg-[#F1DBA6]">Découvrir mon avantage</a>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function AmbSteps() {
  return (
    <section id="fonctionnement" data-testid="amb-steps" className={`${A.ivory} scroll-mt-24 px-5 py-20 md:px-10 md:py-28`}>
      <div className="mx-auto max-w-6xl">
        <Reveal><Eyebrow>En 4 étapes</Eyebrow><h2 className={`font-display text-3xl md:text-4xl ${A.ink}`}>Comment ça fonctionne ?</h2></Reveal>
        <ol className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <li data-testid={`amb-step-${i + 1}`} className={`h-full rounded-2xl border ${A.border} bg-white p-6`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#7A5418] font-display text-xl text-[#7A5418]">{i + 1}</span>
                <h3 className={`mt-5 font-display text-xl ${A.ink}`}>{s.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${A.muted}`}>{s.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function AmbProfile() {
  return (
    <section data-testid="amb-profile" className={`${A.bg} px-5 py-20 md:px-10 md:py-28`}>
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        <img src="/editorial/cheveux-afro-boucles-volumineux.webp" alt="Ambassadrice Ozentya aux cheveux afro" loading="lazy" className="aspect-[4/5] w-full rounded-[22px] object-cover" />
        <Reveal>
          <Eyebrow>Le profil recherché</Eyebrow>
          <h2 className={`font-display text-3xl md:text-4xl ${A.ink}`}>Et si tu devenais ambassadeur Ozentya ?</h2>
          <p data-testid="amb-afro-specialist" className="mt-6 rounded-2xl border-l-4 border-[#7A5418] bg-[#F1E4CF] p-4 text-base leading-relaxed text-[#141013]"><b>Spécialistes du cheveu afro recherchés.</b> Nos ambassadeurs doivent être spécialisés dans le cheveu afro, crépu ou bouclé.</p>
          <p className={`mt-6 text-base leading-relaxed ${A.muted}`}>Nous recherchons des personnes authentiques, créatives et motivées, passionnées par le cheveu afro, qui aiment partager leurs découvertes et créer du contenu sur les réseaux sociaux.</p>
          <ul className="mt-8 space-y-3">
            {PROFILE.map((p) => <li key={p} className={`flex items-start gap-3 text-base ${A.ink}`}><Check className="mt-1 h-4 w-4 shrink-0 text-[#7A5418]" />{p}</li>)}
          </ul>
          <p data-testid="amb-no-minimum" className={`mt-6 text-sm ${A.muted}`}>Aucun nombre minimum d'abonnés n'est exigé : chaque profil est étudié par Ozentya.</p>
          <a href="#candidature" data-testid="amb-profile-cta" className={`${btnDark} mt-8`}>Je candidate</a>
        </Reveal>
      </div>
    </section>
  );
}
